import store from '@/store/store';
import { getRgbaFromHex } from '@/utils/string';

// helper function that takes a CSS transform string (e.g. "transform(50px, 100px)")
// ... and returns it as an object with "x" and "y" props containing the numeric values (e.g. { x: 50, y: 100 })
export function getTransformValues(transformStr) {
    if (!transformStr) return null;

    const openParenIndex = transformStr.indexOf('(');
    const closeParenIndex = transformStr.indexOf(')');
    const commaIndex = transformStr.indexOf(',');

    // sample transformStr = "transform(50px, 100px)"
    const transformX = transformStr.slice(openParenIndex + 1, commaIndex);
    const transformY = transformStr.slice(commaIndex + 2, closeParenIndex);

    // strip the "px" and convert to a number
    return {
        x: +transformX.slice(0, -2),
        y: +transformY.slice(0, -2),
    };
}

// returns a background image style object for the specified element
export function backgroundImageStyle(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    let backgroundImageUrl = element.getElementData('background-image-url');
    let backgroundOverlayColor = element.getElementData('background-overlay');
    const backgroundAttachment = element.getElementData('background-attachment');
    const backgroundPosition = element.getElementData('background-position');
    const backgroundSize = element.getElementData('background-size') || 'cover';
    const overlayOpacity = element.getElementData('background-overlay-opacity') || '0.75';
    const backgroundRepeat = element.getElementData('background-repeat') || 'no-repeat';

    if (!backgroundImageUrl) return null;

    if (process.env.VUE_APP_ENV !== 'public' && !backgroundImageUrl.startsWith('http')) {
        backgroundImageUrl = `${ process.env.VUE_APP_CM_URL }${ backgroundImageUrl }`;
    }
    backgroundOverlayColor = getRgbaFromHex(backgroundOverlayColor, overlayOpacity);
    // pre-pending 'linear-gradient' allows us to apply a color overlay on the background image; we use rgba instead of hex to apply transparency:
    const linearGradient = `linear-gradient(${ backgroundOverlayColor }, ${ backgroundOverlayColor })`;

    return {
        backgroundImage: `${ linearGradient }, url(${ backgroundImageUrl })`,
        backgroundAttachment,
        backgroundPosition,
        backgroundSize,
        backgroundRepeat,
    };
}

// returns a floating nav style object for the specified element
export function floatingNavStyle(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    const inlineStyleStr = element.getElementData('floating-nav');

    if (!inlineStyleStr) return null;

    const styleObject = inlineStyleStr.split(';')
        .slice(0, -1) // the last array item is an empty string
        .map(dec => dec.trim()) // trim all whitespace
        .reduce((obj, dec) => { // now generate the style object
            const colonIndex = dec.indexOf(':');
            const key = dec.slice(0, colonIndex);
            const value = dec.slice(colonIndex + 2);

            obj[key] = value;

            return obj;
        }, {});

    return styleObject;
}

// returns a gradient style object for the specified gradient name
export function gradientStyleByName(gradientName) {
    if (!gradientName) return null;

    const gradient = store.state.settings.gradients.find(g => g.name === gradientName);

    if (!gradient) return null;

    const { type, direction, points } = gradient;

    let positionString = `${ points[0].color } ${ points[0].position }, ${ points[1].color } ${ points[1].position }`;

    for (let i = 2, len = points.length; i < len; i++) {
        positionString += `, ${ points[i].color } ${ points[i].position }`;
    }

    return {
        backgroundImage: `${ type }-gradient(${ direction }deg, ${ positionString })`,
    };
}

// returns a gradient style object for the specified element
export function gradientStyleByElement(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    const gradientName = element.getElementData('gradient');

    return gradientStyleByName(gradientName);
}

// returns an opacity style object for the specified element
export function opacityStyleByElement(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    const gradientOverlayOpacity = element.getElementData('gradient-overlay-opacity') || 1;

    return { opacity: gradientOverlayOpacity };
}

// returns a gradient style object for the specified element, at the specified index
// ... this is utilized by componenents that need to manage multiple gradients at a single time, such as the fancy carousel
export function gradientStyleByIndex(element, index) {
    if (!element
        || typeof element.getElementData !== 'function'
        || typeof index !== 'number') {
        return null;
    }

    const gradientName = element.getElementData('gradients')[index];

    return gradientStyleByName(gradientName);
}

// returns a text shadow style object for the specified element
export function offsetStyle(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    const offset = element.getElementData('offset');

    if (!offset) return;

    return {
        transform: `translate(${ offset.x }px, ${ offset.y }px)`,
    };
}

// returns the "offset" (top and left) styles for the specified element
export function textShadowStyle(element) {
    if (!element || typeof element.getElementData !== 'function') return null;

    return {
        textShadow: element.getElementData('text-shadow'),
    };
}
